import { useState, useEffect, useMemo, useCallback } from 'react';
import { Message, TimelineItem } from '@/features/messages/messages.types';
import { ExtendedTimelineItem } from '../conversations.types';
import {
  sortMessagesByTimestamp,
  deduplicateMessages,
  filterTimelineByConversation,
  messageToTimelineItem,
} from '../components/conversation-message-utils';

interface UseMessageStateProps {
  conversationId: string;
  timeline: TimelineItem[];
  initialPendingMessages?: Message[];
}

interface UseMessageStateReturn {
  combinedTimeline: ExtendedTimelineItem[];
  pendingMessages: Message[];
  addPendingMessage: (message: Message) => void;
  updatePendingMessage: (messageId: string, updates: Partial<Message>) => void;
  removePendingMessage: (messageId: string) => void;
  clearPendingMessages: () => void;
}

export const useMessageState = ({
  conversationId,
  timeline,
  initialPendingMessages = [],
}: UseMessageStateProps): UseMessageStateReturn => {
  const [pendingMessages, setPendingMessages] = useState<Message[]>(
    initialPendingMessages,
  );

  // Filter timeline to only include messages for current conversation
  const conversationTimeline = useMemo(
    () => filterTimelineByConversation(timeline, conversationId),
    [timeline, conversationId],
  );

  // Deduplicate pending messages against timeline
  const filteredPendingMessages = useMemo(
    () =>
      deduplicateMessages(
        conversationTimeline,
        pendingMessages,
        conversationId,
      ),
    [conversationTimeline, pendingMessages, conversationId],
  );

  // Combine timeline and pending messages, then sort
  const combinedTimeline = useMemo(() => {
    const timelineItems = conversationTimeline as ExtendedTimelineItem[];
    const pendingItems = filteredPendingMessages.map(messageToTimelineItem);

    const combined = [...timelineItems, ...pendingItems];
    const sorted = sortMessagesByTimestamp(combined);

    return sorted;
  }, [conversationTimeline, filteredPendingMessages]);

  // Clean up pending messages when they appear in timeline
  useEffect(() => {
    if (conversationTimeline.length === 0) return;

    setPendingMessages((prev) => {
      const timelineMessageIds = new Set(
        conversationTimeline
          .filter((item) => item.type === 'message')
          .map((item) => (item.content as Message).id),
      );

      const timelineTempIds = new Set(
        conversationTimeline
          .filter((item) => item.type === 'message')
          .map((item) => (item.content as Message)._tempId)
          .filter(Boolean),
      );

      return prev.filter((msg) => {
        // Remove if message ID is now in timeline
        if (timelineMessageIds.has(msg.id)) return false;

        // Remove if tempId matches a timeline message ID
        if (msg._tempId && timelineMessageIds.has(msg._tempId)) return false;

        // Remove if message ID matches a timeline tempId
        if (timelineTempIds.has(msg.id)) return false;

        return true;
      });
    });
  }, [conversationTimeline]);

  const addPendingMessage = useCallback((message: Message) => {
    setPendingMessages((prev) => [...prev, message]);
  }, []);

  const updatePendingMessage = useCallback(
    (messageId: string, updates: Partial<Message>) => {
      setPendingMessages((prev) =>
        prev.map((msg) =>
          msg.id === messageId ? { ...msg, ...updates } : msg,
        ),
      );
    },
    [],
  );

  const removePendingMessage = useCallback((messageId: string) => {
    setPendingMessages((prev) => prev.filter((msg) => msg.id !== messageId));
  }, []);

  const clearPendingMessages = useCallback(() => {
    setPendingMessages([]);
  }, []);

  return {
    combinedTimeline,
    pendingMessages: filteredPendingMessages,
    addPendingMessage,
    updatePendingMessage,
    removePendingMessage,
    clearPendingMessages,
  };
};
